import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { FileText } from "lucide-mui";
import { api, type CompanyFinancial } from "../../../api/client.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import DropdownMenu from "../../../components/DropdownMenu.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import ExpandableRow from "../../../components/table/ExpandableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { formatDate, formatNumber } from "../../../utils/format.ts";
import SectionCard from "../../../components/SectionCard.tsx";

interface FinancialsCardProps {
  companyId: string;
  financials: CompanyFinancial[] | undefined;
  isLoading: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  annual_report: "Annual report",
  auditor_report: "Auditor report",
};

type NumberField = {
  [K in keyof CompanyFinancial]: CompanyFinancial[K] extends number | null ? K : never;
}[keyof CompanyFinancial];

// Shown in the expanded row, when the filing has them.
const DETAIL_GROUPS: { title: string; fields: [NumberField, string][] }[] = [
  {
    title: "Result",
    fields: [
      ["profit_before_tax", "Profit before tax"],
      ["net_financial_items", "Net financial items"],
      ["personnel_costs", "Personnel costs"],
      ["tax_expense", "Tax"],
      ["employees_avg", "Employees (avg)"],
      ["proposed_dividend", "Proposed dividend"],
    ],
  },
  {
    title: "Assets",
    fields: [
      ["cash", "Cash"],
      ["receivables", "Receivables"],
      ["inventory", "Inventory"],
      ["current_assets", "Current assets"],
      ["fixed_assets", "Fixed assets"],
      ["pledged_assets", "Pledged assets"],
    ],
  },
  {
    title: "Equity & liabilities",
    fields: [
      ["share_capital", "Share capital"],
      ["current_liabilities", "Current liabilities"],
      ["long_term_liabilities", "Long-term liabilities"],
      ["total_liabilities", "Total liabilities"],
    ],
  },
];

const FRAMEWORK_LABELS: Record<string, string> = {
  k2: "K2",
  k3: "K3",
  ngaap_small: "NGAAP (small company)",
  ngaap: "NGAAP",
  simplified_ifrs: "Simplified IFRS",
  ifrs: "IFRS",
};

const OPINION_LABELS: Record<string, string> = {
  standard: "Standard",
  deviating: "Deviates from standard wording",
  rejected: "Advised against adoption",
};

// The annual report's own words, in reading order.
const REPORT_TEXT_LABELS: [string, string][] = [
  ["significant_events", "Significant events in the year"],
  ["events_after_year_end", "Events after year-end"],
  ["going_concern", "Going concern (auditor)"],
  ["audit_remark", "Auditor's remark"],
  ["outlook", "Outlook and risks"],
  ["multi_year_comment", "Multi-year overview"],
  ["dividend_statement", "Board on the dividend"],
  ["parent_company_note", "Parent company"],
];

function formatRatio(value: number | null): string {
  return value == null ? "-" : `${(value * 100).toFixed(1)} %`;
}

function yesNo(value: boolean | null): string | null {
  return value == null ? null : value ? "Yes" : "No";
}

function FinancialDetails({ f }: { f: CompanyFinancial }) {
  const facts: [string, string | null][] = [
    ["Audited", yesNo(f.audited)],
    [
      "Accounting framework",
      f.accounting_framework
        ? (FRAMEWORK_LABELS[f.accounting_framework] ?? f.accounting_framework)
        : null,
    ],
    [
      "Audit opinion",
      f.audit_opinion ? (OPINION_LABELS[f.audit_opinion] ?? f.audit_opinion) : null,
    ],
    ["AGM", f.agm_date ? formatDate(f.agm_date) : null],
    ["Group parent", yesNo(f.parent_company)],
    ["Break-up basis", f.liquidation_basis ? "Yes" : null],
  ];
  const texts = REPORT_TEXT_LABELS.filter(([key]) => f.report_texts?.[key]).map(
    ([key, label]) => [label, f.report_texts![key]] as [string, string],
  );
  const groups = DETAIL_GROUPS.map((g) => ({
    title: g.title,
    rows: g.fields
      .filter(([key]) => f[key] != null)
      .map(([key, label]) => [label, formatNumber(f[key])] as [string, string]),
  })).filter((g) => g.rows.length > 0);
  const other = facts.filter((fact): fact is [string, string] => fact[1] != null);
  if (other.length > 0) groups.push({ title: "Other", rows: other });
  if (groups.length === 0 && texts.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No further figures in this filing.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        }}
      >
        {groups.map((g) => (
          <Box key={g.title}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {g.title}
            </Typography>
            {g.rows.map(([label, value]) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      {texts.map(([label, value]) => (
        <Box key={label}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function FinancialsCard({
  companyId,
  financials,
  isLoading,
}: FinancialsCardProps) {
  return (
    <SectionCard title="Financials">
      {isLoading && <CenteredSpinner py={3} />}
      {financials && financials.length === 0 && (
        <EmptyState title="No financial data yet" description="No annual accounts on record for this company" />
      )}
      {financials && financials.length > 0 && (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Year</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Operating result</TableCell>
              <TableCell align="right">Net income</TableCell>
              <TableCell align="right">Total assets</TableCell>
              <TableCell align="right">Total equity</TableCell>
              <TableCell align="right">Equity ratio</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell padding="none" />
            </TableRow>
          </TableHead>
          <TableBody>
            {financials.map((f) => (
              <ExpandableRow
                key={f.id}
                colSpan={11}
                cells={
                  <>
                    <TableCell>{f.fiscal_year}</TableCell>
                    <TableCell>{f.report_type}</TableCell>
                    <TableCell align="right">{formatNumber(f.revenue)}</TableCell>
                    <TableCell align="right">{formatNumber(f.operating_result)}</TableCell>
                    <TableCell align="right">{formatNumber(f.net_income)}</TableCell>
                    <TableCell align="right">{formatNumber(f.total_assets)}</TableCell>
                    <TableCell align="right">{formatNumber(f.total_equity)}</TableCell>
                    <TableCell align="right">{formatRatio(f.equity_ratio)}</TableCell>
                    <TableCell>{f.currency}</TableCell>
                    <TableCell padding="none">
                      {f.source_files && f.source_files.length > 0 && (
                        <DropdownMenu
                          aria-label="View source files"
                          tooltip="View source files"
                          icon={<FileText fontSize="small" />}
                          align="right"
                        >
                          {f.source_files.map((file) => (
                            <MenuItem
                              key={file.s3_key}
                              component="a"
                              href={api.companyFinancialFileUrl(companyId, f.id, file.filename)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {(ROLE_LABELS[file.role] ?? file.role) + " — " + file.filename}
                            </MenuItem>
                          ))}
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </>
                }
              >
                <FinancialDetails f={f} />
              </ExpandableRow>
            ))}
          </TableBody>
        </ScrollTable>
      )}
    </SectionCard>
  );
}
